import * as XLSX from 'xlsx';

// ============================================================
// Zifek BI — moteur d'analyse des données côté navigateur.
// Import CSV / XLSX / XLS / JSON, détection automatique des types,
// statistiques de colonnes et agrégations pour les graphiques.
// ============================================================

export type ColumnType = 'number' | 'date' | 'boolean' | 'string';

export interface ColumnMeta {
  name: string;
  type: ColumnType;
  nullCount: number;
  uniqueCount: number;
  sampleValues: string[];
}

export type Row = Record<string, string | number | boolean | null>;

export interface ParsedDataset {
  columns: ColumnMeta[];
  rows: Row[];
}

const MAX_ROWS = 5000;

function normalizeHeader(h: unknown, index: number): string {
  const s = String(h ?? '').trim();
  return s || `Colonne ${index + 1}`;
}

function isNumeric(v: unknown): boolean {
  if (v === null || v === undefined || v === '') return false;
  if (typeof v === 'number') return Number.isFinite(v);
  const s = String(v).replace(/\s/g, '').replace(/[€$£]|MAD|DH/gi, '').replace(',', '.');
  return s !== '' && !Number.isNaN(Number(s));
}

function isDateLike(v: unknown): boolean {
  if (v instanceof Date) return true;
  const s = String(v ?? '').trim();
  if (!s) return false;
  // Formats : 2024-01-15, 15/01/2024, 15-01-2024, 01/15/2024
  if (/^\d{4}-\d{1,2}-\d{1,2}/.test(s)) return !Number.isNaN(Date.parse(s));
  if (/^\d{1,2}[/-]\d{1,2}[/-]\d{2,4}/.test(s)) return true;
  return false;
}

function toNumber(v: unknown): number {
  if (typeof v === 'number') return v;
  const s = String(v ?? '').replace(/\s/g, '').replace(/[€$£]|MAD|DH/gi, '').replace(',', '.');
  const n = Number(s);
  return Number.isNaN(n) ? 0 : n;
}

/** Détecte le type dominant d'une colonne à partir de ses valeurs. */
function detectType(values: unknown[]): ColumnType {
  let num = 0;
  let date = 0;
  let bool = 0;
  let total = 0;
  values.forEach((v) => {
    if (v === null || v === undefined || v === '') return;
    total += 1;
    if (typeof v === 'boolean' || /^(true|false|oui|non)$/i.test(String(v))) bool += 1;
    else if (isNumeric(v)) num += 1;
    else if (isDateLike(v)) date += 1;
  });
  if (total === 0) return 'string';
  if (bool / total > 0.8) return 'boolean';
  if (num / total > 0.7) return 'number';
  if (date / total > 0.7) return 'date';
  return 'string';
}

/** Construit les métadonnées de colonnes + convertit les lignes. */
export function analyzeRows(headers: string[], rawRows: unknown[][]): ParsedDataset {
  const trimmed = rawRows.slice(0, MAX_ROWS);
  const columns: ColumnMeta[] = headers.map((name, ci) => {
    const values = trimmed.map((r) => r[ci]);
    const type = detectType(values);
    const nonNull = values.filter((v) => v !== null && v !== undefined && v !== '');
    const unique = new Set(nonNull.map((v) => String(v)));
    return {
      name,
      type,
      nullCount: values.length - nonNull.length,
      uniqueCount: unique.size,
      sampleValues: Array.from(unique).slice(0, 3),
    };
  });

  const rows: Row[] = trimmed.map((r) => {
    const obj: Row = {};
    headers.forEach((h, ci) => {
      const v = r[ci];
      const col = columns[ci];
      if (v === null || v === undefined || v === '') obj[h] = null;
      else if (col.type === 'number') obj[h] = toNumber(v);
      else if (col.type === 'boolean') obj[h] = /^(true|oui)$/i.test(String(v));
      else obj[h] = String(v);
    });
    return obj;
  });

  return { columns, rows };
}

/** Lit un fichier CSV / XLSX / XLS / JSON et renvoie les données analysées. */
export async function parseFile(file: File): Promise<ParsedDataset> {
  const ext = file.name.split('.').pop()?.toLowerCase() || '';
  if (ext === 'json') {
    const text = await file.text();
    const parsed = JSON.parse(text);
    const arr: Record<string, unknown>[] = Array.isArray(parsed)
      ? parsed
      : (Array.isArray(parsed?.data) ? parsed.data : []);
    if (arr.length === 0) throw new Error('Le fichier JSON ne contient pas de tableau de données.');
    const headers = Array.from(new Set(arr.flatMap((o) => Object.keys(o))));
    const rawRows = arr.map((o) => headers.map((h) => o[h]));
    return analyzeRows(headers, rawRows);
  }

  const buf = await file.arrayBuffer();
  const workbook = XLSX.read(buf, { type: 'array', cellDates: true });
  const sheet = workbook.Sheets[workbook.SheetNames[0]];
  const matrix: unknown[][] = XLSX.utils.sheet_to_json(sheet, { header: 1, raw: false, defval: '' });
  if (matrix.length < 2) throw new Error('Le fichier ne contient pas assez de lignes.');
  const headers = (matrix[0] as unknown[]).map((h, i) => normalizeHeader(h, i));
  return analyzeRows(headers, matrix.slice(1));
}

// ── Agrégations ──
export type Aggregation = 'SUM' | 'AVG' | 'COUNT' | 'MIN' | 'MAX' | 'DISTINCT';

export function aggregate(rows: Row[], column: string, agg: Aggregation): number {
  if (agg === 'COUNT') return rows.length;
  if (agg === 'DISTINCT') return new Set(rows.map((r) => r[column])).size;
  const nums = rows.map((r) => toNumber(r[column])).filter((n) => Number.isFinite(n));
  if (nums.length === 0) return 0;
  switch (agg) {
    case 'SUM': return nums.reduce((s, n) => s + n, 0);
    case 'AVG': return nums.reduce((s, n) => s + n, 0) / nums.length;
    case 'MIN': return Math.min(...nums);
    case 'MAX': return Math.max(...nums);
    default: return 0;
  }
}

export function groupBy(
  rows: Row[],
  dimension: string,
  measure: string,
  agg: Aggregation,
): { label: string; value: number }[] {
  const groups = new Map<string, Row[]>();
  rows.forEach((r) => {
    const key = String(r[dimension] ?? '(vide)');
    const arr = groups.get(key) || [];
    arr.push(r);
    groups.set(key, arr);
  });
  return Array.from(groups.entries())
    .map(([label, groupRows]) => ({ label, value: Math.round(aggregate(groupRows, measure, agg) * 100) / 100 }))
    .sort((a, b) => b.value - a.value);
}

export interface BiInsight {
  title: string;
  measure: string;
  dimension: string;
  agg: Aggregation;
  chartType: 'bar' | 'line' | 'pie' | 'column' | 'area';
  points: { label: string; value: number }[];
}

export interface AutoDashboard {
  kpis: { label: string; value: number; column: string; agg: Aggregation }[];
  charts: BiInsight[];
  numericColumns: string[];
  dimensionColumns: string[];
}

/**
 * Génère automatiquement un tableau de bord à partir des données :
 * choisis les mesures numériques et les dimensions catégorielles/dates
 * et produit KPI + graphiques pertinents.
 */
export function buildAutoDashboard(columns: ColumnMeta[], rows: Row[]): AutoDashboard {
  const numericColumns = columns.filter((c) => c.type === 'number').map((c) => c.name);
  const dimensionColumns = columns
    .filter((c) => (c.type === 'string' || c.type === 'date' || c.type === 'boolean') && c.uniqueCount > 1 && c.uniqueCount <= 30)
    .map((c) => c.name);

  // KPI sur les mesures numériques + nombre de lignes
  const kpis: AutoDashboard['kpis'] = numericColumns.slice(0, 3).map((c) => ({
    label: `Total ${c}`,
    value: Math.round(aggregate(rows, c, 'SUM') * 100) / 100,
    column: c,
    agg: 'SUM' as Aggregation,
  }));
  kpis.unshift({ label: 'Lignes de données', value: rows.length, column: '', agg: 'COUNT' });

  const charts: BiInsight[] = [];
  const primaryMeasure = numericColumns[0];
  const primaryDim = dimensionColumns[0];

  if (primaryDim && primaryMeasure) {
    charts.push({
      title: `${primaryMeasure} par ${primaryDim}`,
      measure: primaryMeasure,
      dimension: primaryDim,
      agg: 'SUM',
      chartType: dimensionColumns.length > 6 ? 'line' : 'bar',
      points: groupBy(rows, primaryDim, primaryMeasure, 'SUM').slice(0, 20),
    });
  }
  if (dimensionColumns[1] && primaryMeasure) {
    charts.push({
      title: `Répartition de ${primaryMeasure} par ${dimensionColumns[1]}`,
      measure: primaryMeasure,
      dimension: dimensionColumns[1],
      agg: 'SUM',
      chartType: 'pie',
      points: groupBy(rows, dimensionColumns[1], primaryMeasure, 'SUM').slice(0, 8),
    });
  }
  if (numericColumns[1] && primaryDim) {
    charts.push({
      title: `${numericColumns[1]} par ${primaryDim}`,
      measure: numericColumns[1],
      dimension: primaryDim,
      agg: 'SUM',
      chartType: 'column',
      points: groupBy(rows, primaryDim, numericColumns[1], 'SUM').slice(0, 20),
    });
  }

  return { kpis, charts, numericColumns, dimensionColumns };
}

/** Résumé compact des données à transmettre à l'IA (colonnes + agrégats). */
export function buildDataSummary(datasetName: string, columns: ColumnMeta[], rows: Row[]): string {
  const lines: string[] = [];
  lines.push(`Source : ${datasetName}`);
  lines.push(`Nombre de lignes : ${rows.length}`);
  lines.push('Colonnes :');
  columns.forEach((c) => {
    lines.push(`- ${c.name} (${c.type}, ${c.nullCount} vides, ${c.uniqueCount} valeurs distinctes)`);
  });
  const numeric = columns.filter((c) => c.type === 'number').map((c) => c.name);
  if (numeric.length > 0) {
    lines.push('Totaux des colonnes numériques :');
    numeric.slice(0, 8).forEach((n) => {
      lines.push(`- ${n} : somme=${Math.round(aggregate(rows, n, 'SUM') * 100) / 100}, moyenne=${Math.round(aggregate(rows, n, 'AVG') * 100) / 100}, min=${aggregate(rows, n, 'MIN')}, max=${aggregate(rows, n, 'MAX')}`);
    });
  }

  // Répartitions réelles par dimension (pour que l'IA ne réponde pas au hasard).
  const dims = columns
    .filter((c) => (c.type === 'string' || c.type === 'date' || c.type === 'boolean') && c.uniqueCount > 1 && c.uniqueCount <= 40)
    .slice(0, 3);
  const mainMeasure = numeric[0];
  if (mainMeasure) {
    dims.forEach((d) => {
      const groups = groupBy(rows, d.name, mainMeasure, 'SUM').slice(0, 10);
      if (groups.length === 0) return;
      lines.push(`Répartition de ${mainMeasure} par ${d.name} (top ${groups.length}) :`);
      groups.forEach((g) => lines.push(`- ${g.label} : ${g.value}`));
    });
  }
  return lines.join('\n');
}

// ── Relations entre sources (colonnes communes) ──────────────

export interface BiSourceMeta {
  id: number;
  name: string;
  columns: ColumnMeta[];
}

export interface BiRelation {
  id: string;
  sourceId: number;
  sourceName: string;
  sourceColumn: string;
  targetId: number;
  targetName: string;
  targetColumn: string;
  /** Type de la colonne commune (nombre / texte / date). */
  type: ColumnType;
}

function normalizeColumn(c: string): string {
  return c.toLowerCase().replace(/[\s_-]+/g, '');
}

/**
 * Détecte automatiquement les colonnes communes entre plusieurs sources.
 * Utilisé pour proposer des relations entre fichiers (style BI).
 */
export function detectRelations(sources: BiSourceMeta[]): BiRelation[] {
  const relations: BiRelation[] = [];
  const seen = new Set<string>();
  for (let i = 0; i < sources.length; i += 1) {
    for (let j = i + 1; j < sources.length; j += 1) {
      const a = sources[i];
      const b = sources[j];
      a.columns.forEach((ca) => {
        const match = b.columns.find((cb) => normalizeColumn(cb.name) === normalizeColumn(ca.name));
        if (!match) return;
        // Évite les faux positifs sur colonnes trop génériques ou vides.
        if (ca.uniqueCount <= 1 && match.uniqueCount <= 1) return;
        const key = `${a.id}:${ca.name}->${b.id}:${match.name}`;
        if (seen.has(key)) return;
        seen.add(key);
        relations.push({
          id: key,
          sourceId: a.id,
          sourceName: a.name,
          sourceColumn: ca.name,
          targetId: b.id,
          targetName: b.name,
          targetColumn: match.name,
          type: ca.type,
        });
      });
    }
  }
  return relations;
}

// ── Champs calculés ──────────────────────────────────────────

const NUMERIC_OP_RE = /^[\d\s+\-*/().]+$/;

/**
 * Transforme une formule utilisateur en expression JS sûre.
 * Les noms de colonnes sont remplacés par un accès numérique à la ligne.
 * Renvoie null si la formule contient des caractères interdits.
 */
export function buildFormulaExpression(expression: string, columnNames: string[]): string | null {
  let out = expression;
  [...columnNames]
    .sort((a, b) => b.length - a.length)
    .forEach((cn) => {
      const escaped = cn.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      out = out.replace(new RegExp(`\\b${escaped}\\b`, 'g'), `Number(row[${JSON.stringify(cn)}] || 0)`);
    });
  const stripped = out.replace(/Number\(row\["[^"]*"\] \|\| 0\)/g, '1');
  if (!NUMERIC_OP_RE.test(stripped)) return null;
  return out;
}

/** Évalue une formule pour chaque ligne et renvoie les nouvelles données. */
export function computeCalculatedField(
  rows: Row[],
  columnNames: string[],
  name: string,
  expression: string,
): { rows: Row[]; column: ColumnMeta } | null {
  const expr = buildFormulaExpression(expression, columnNames);
  if (!expr || !name.trim()) return null;
  let fn: (row: Row) => number;
  try {
    // eslint-disable-next-line no-new-func
    fn = new Function('row', `return (${expr});`) as (row: Row) => number;
  } catch {
    return null;
  }
  const newName = name.trim();
  const newRows: Row[] = rows.map((r) => {
    let value: number;
    try {
      const v = fn(r);
      value = Number.isFinite(v) ? Math.round(v * 100) / 100 : 0;
    } catch {
      value = 0;
    }
    return { ...r, [newName]: value };
  });
  const values = newRows.map((r) => r[newName]);
  const nonNull = values.filter((v) => v !== null && v !== undefined && v !== '');
  const column: ColumnMeta = {
    name: newName,
    type: 'number',
    nullCount: values.length - nonNull.length,
    uniqueCount: new Set(nonNull.map(String)).size,
    sampleValues: Array.from(new Set(nonNull.map(String))).slice(0, 3),
  };
  return { rows: newRows, column };
}